import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FiCamera } from "react-icons/fi";
import Avator from "../../../assets/avatar.png";
import { BsCheckCircleFill } from "react-icons/bs";

export default function CompanyProfile() {
  const [kycCompleted, setKycCompleted] = useState(false);
  const [userData, setUserData] = useState({
    userId: "",
    company: "",
    email: "",
    name: "",
    phoneNumber: "",
    monthlyOrders: "0",
  });

  const [imagePreview, setImagePreview] = useState(Avator);
  const [profileImage, setProfileImage] = useState(null);
  const [brandName, setBrandName] = useState("");
  const [website, setWebsite] = useState("");

  const hasFetched = useRef(false);
  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchUserData = async () => {
      try {
        const token = document.cookie.split("=")[1];
        const response = await axios.get(`${REACT_APP_BACKEND_URL}/order/getUser`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setUserData((prev) => ({
          ...prev,
          userId: response.data.userId,
          name: response.data.fullname,
          company: response.data.company,
          email: response.data.email,
          phoneNumber: response.data.phoneNumber,
          monthlyOrders: response.data.monthlyOrders || "0",
          brandName: response.data.brandName || "",
          website: response.data.website || "",
        }));
        setImagePreview(response.data.profileImage || Avator);
        setKycCompleted(response.data.kycDone);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      setImagePreview(imageURL);
      setProfileImage(file);
    }
  };

  const handleSave = async () => {
    try {
      const token = document.cookie.split("=")[1];
      const formData = new FormData();
      formData.append("brandName", brandName);
      formData.append("website", website);
      if (profileImage) formData.append("profileImage", profileImage);

      await axios.post(`${REACT_APP_BACKEND_URL}/user/update-profile`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Profile updated successfully");
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Failed to save profile");
    }
  };

  return (
    <div className="w-full sm:px-2 p-1">
      {/* Header */}
      <div className="relative w-full sm:h-40 h-28 bg-green-100 flex items-end justify-center rounded-t-lg">
        <div className="relative -mb-10">
          <img
            src={imagePreview}
            alt="profile"
            className="w-24 h-24 rounded-full border-4 border-white object-cover"
          />
          <label
            htmlFor="profileUpload"
            className="absolute bottom-0 right-0 bg-white p-1 rounded-full cursor-pointer shadow-md"
          >
            <FiCamera className="text-gray-600 text-xl" />
          </label>
          <input
            type="file"
            id="profileUpload"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>
      </div>

      {/* Profile Info */}
      <div className="mt-12 w-full">
        <h2 className="text-[12px] sm:text-[18px] font-[600] text-center text-gray-700 flex items-center justify-center gap-1">
          {userData.name}
          {kycCompleted && (
            <BsCheckCircleFill className="text-[#0CBB7D] text-[18px]" title="Verified" />
          )}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4 mt-2">

          {/* Summary Card */}
          <div className="space-y-2 bg-white p-4 text-gray-500 text-[12px] sm:text-[14px] border-gray-300 rounded-lg border">
            <p>
              <strong>User ID:</strong>{" "}
              <span className="text-gray-700">{userData.userId}</span>
            </p>
            <p>
              <strong>KYC Status:</strong>{" "}
              <span className={`font-[600] ${kycCompleted ? "text-[#0CBB7D]" : "text-red-500"}`}>
                {kycCompleted ? "Complete" : "Pending"}
              </span>
            </p>
            <p>
              <strong>Target:</strong>{" "}
              <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">
                {userData.monthlyOrders} SHIPMENTS
              </span>
            </p>
            <p>
              <strong>Agreement:</strong>{" "}
              <span className={`font-[600] ${kycCompleted ? "text-[#0CBB7D]" : "text-red-500"}`}>
                {kycCompleted ? "ACCEPTED" : "NOT ACCEPTED"}
              </span>
            </p>
          </div>

          {/* Details Form */}
          <div className="w-full">
            {/* Mobile View Cards */}
            <div className="md:hidden bg-white border border-gray-300 p-4 rounded-lg space-y-1 font-[600] text-[12px]">
              {[
                { label: "Name", value: userData.name },
                { label: "Email", value: userData.email },
                { label: "Mobile", value: userData.phoneNumber },
                { label: "Company", value: userData.company },
                { label: "Brand Name", value: userData.brandName },
                { label: "Website", value: userData.website },
              ].map((item, index) => (
                <div key={index} className="flex justify-between pb-1 last:border-b-0">
                  <span className="font-[600] text-gray-500">{item.label}</span>
                  <span className="text-gray-500 text-right max-w-[50%]">{item.value || "-"}</span>
                </div>
              ))}
            </div>


            {/* Desktop View Form */}
            <form className="hidden md:grid grid-cols-1 w-full p-4 border rounded-lg border-gray-300 bg-white sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[12px] text-gray-500 sm:text-[14px]">Name</label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 focus:outline-none text-gray-700 bg-gray-200 rounded-lg text-[12px]"
                  value={userData.name}
                  readOnly
                />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 sm:text-[14px]">Email</label>
                <input
                  type="email"
                  className="w-full mt-1 px-3 py-2 focus:outline-none text-gray-700 bg-gray-200 rounded-lg text-[12px]"
                  value={userData.email}
                  readOnly
                />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 sm:text-[14px]">Mobile</label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 focus:outline-none text-gray-700 bg-gray-200 rounded-lg text-[12px]"
                  value={userData.phoneNumber}
                  readOnly
                />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 sm:text-[14px]">Company</label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 focus:outline-none text-gray-700 bg-gray-200 rounded-lg text-[12px]"
                  value={userData.company}
                  readOnly
                />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 sm:text-[14px]">Brand Name</label>
                <input
                  type="text"
                  value={userData.brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  className="w-full mt-1 px-3 py-2 focus:outline-none text-gray-700 bg-white border-2 rounded-lg text-[12px]"
                />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 sm:text-[14px]">Website</label>
                <input
                  type="text"
                  value={userData.website}
                  onChange={(e) => setWebsite(e.target.value)}
                  className="w-full mt-1 px-3 py-2 focus:outline-none text-gray-700 bg-white border-2 rounded-lg text-[12px]"
                />
              </div>
            </form>
          </div>

        </div>
      </div>
      {/* POC and Sales Executive Details - Two Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:text-[14px] text-[12px] text-gray-500 font-[600] mt-4">
        {/* POC Details */}
        <div className="p-4 border border-gray-300 rounded-lg shadow-sm bg-green-100">
          <h2 className="mb-3 text-[14px] sm:text-[16px]">POC Details</h2>
          <div>
            <label className="block font-[600]">POC Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              className="w-full px-3 py-2 font-[400] border-2 border-gray-300 rounded-lg outline-none mt-1 focus:ring-0 text-[14px]"
              placeholder="Enter POC Name"
            />
          </div>

          <div className="mt-4">
            <label className="block font-[600]">POC Email <span className="text-red-500">*</span></label>
            <input
              type="email"
              className="w-full px-3 py-2 font-[400] border-2 border-gray-300 rounded-lg outline-none mt-1 focus:ring-0 text-[14px]"
              placeholder="Enter POC Email"
            />
          </div>

          <div className="mt-4">
            <label className="block font-[600]">POC Phone <span className="text-red-500">*</span></label>
            <input
              type="tel"
              className="w-full px-3 py-2 font-[400] border-2 border-gray-300 rounded-lg outline-none mt-1 focus:ring-0 text-[14px]"
              placeholder="Enter POC Phone"
            />
          </div>
        </div>
        {/* Sales Executive Details */}
        <div className="p-4 border border-gray-200 rounded-lg shadow-sm bg-green-100">
          <h2 className="mb-3 text-[14px] sm:text-[16px]">Sales Executive</h2>
          <div>
            <label className="block font-[600]">Sales Executive Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              className="w-full px-3 py-2 font-[400] border-2 border-gray-300 rounded-lg outline-none mt-1 focus:ring-0 text-[14px]"
              placeholder="Enter Sales Executive Name"
            />
          </div>

          <div className="mt-4">
            <label className="block font-[600]">Sales Executive Email <span className="text-red-500">*</span></label>
            <input
              type="email"
              className="w-full px-3 py-2 font-[400] border-2 border-gray-300 rounded-lg outline-none mt-1 focus:ring-0 text-[14px]"
              placeholder="Enter Sales Executive Email"
            />
          </div>

          <div className="mt-4">
            <label className="block font-[600]">Sales Executive Phone <span className="text-red-500">*</span></label>
            <input
              type="tel"
              className="w-full px-3 py-2 font-[400] border-2 border-gray-300 rounded-lg outline-none mt-1 focus:ring-0 text-[14px]"
              placeholder="Enter Sales Executive Phone"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-6 text-right">
        <button
          onClick={handleSave}
          className="bg-[#0CBB7D] text-white py-2 px-3 rounded-lg text-[10px] sm:text-[12px] font-[600] hover:bg-green-500 transition"
        >
          Save
        </button>
      </div>
    </div>
  );
}
